import { useEffect, useState } from "react";
import { Plus, Edit2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import DragList from "@/components/admin/DragList";
import IconPicker from "@/components/admin/IconPicker";

const empty = { title: "", slug: "", tagline: "", description: "", roles: "", icon: "Users", sort_order: 0, is_active: true };

export default function AdminServices() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/admin/service-categories").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...s }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, sort_order: Number(form.sort_order) || 0 };
    try {
      if (editing) await api.patch(`/admin/service-categories/${editing.id}`, payload);
      else await api.post("/admin/service-categories", payload);
      toast.success(editing ? "Updated" : "Created");
      setOpen(false); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this service category?")) return;
    await api.delete(`/admin/service-categories/${id}`);
    load();
  };

  const onReorder = async (newList, newIds) => {
    setItems(newList);
    try {
      await api.post("/admin/service-categories/reorder", { ids: newIds });
      toast.success("Order updated");
    } catch (err) {
      toast.error("Failed to save order");
      load();
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-services">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900">Service Categories</h1>
          <p className="mt-1 text-sm text-slate-500">The service categories shown on the Home page and the public /services page. Drag rows to reorder.</p>
        </div>
        <Button onClick={openNew} className="bg-primary hover:bg-primary/90" data-testid="svc-new"><Plus className="mr-1 h-4 w-4" />New Service</Button>
      </div>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        {items.length === 0 ? (
          <div className="p-10 text-center text-slate-400">No service categories yet — click <em>New Service</em>.</div>
        ) : (
          <DragList
            items={items}
            getId={(s) => s.id}
            onReorder={onReorder}
            testIdPrefix="svc-row"
            renderRow={(s, handle) => (
              <div className="flex items-start justify-between gap-4 p-4">
                <div className="flex min-w-0 flex-1 items-start">
                  <div className="mt-0.5">{handle}</div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-900">{s.title}</div>
                    {s.tagline && <div className="mt-0.5 text-sm text-secondary">{s.tagline}</div>}
                    <div className="mt-1 line-clamp-2 text-sm text-slate-600">{s.description}</div>
                    {s.roles && <div className="mt-1.5 text-xs text-slate-500"><span className="font-semibold">Roles:</span> {s.roles}</div>}
                    <div className="mt-1 text-xs text-slate-400">Slug: <code>{s.slug}</code> · Icon: <code>{s.icon}</code> · {s.is_active ? "Active" : "Hidden"}</div>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(s)} data-testid={`svc-edit-${s.id}`}><Edit2 className="h-3.5 w-3.5" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(s.id)} data-testid={`svc-delete-${s.id}`}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
                </div>
              </div>
            )}
          />
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit service category" : "New service category"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3" data-testid="svc-form">
            <div>
              <Label>Title *</Label>
              <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Senior Management" />
            </div>
            <div>
              <Label>Tagline (short subtitle)</Label>
              <Input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} placeholder="Vision, strategy & long-term growth" />
            </div>
            <div>
              <Label>Description *</Label>
              <Textarea required rows={5} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Long description of this service category…" />
            </div>
            <div>
              <Label>Roles / positions covered (comma-separated)</Label>
              <Textarea rows={2} value={form.roles} onChange={(e) => setForm({ ...form, roles: e.target.value })} placeholder="CEO, MD, COO, CFO, CHRO, Director, …" />
              <p className="mt-1 text-[11px] text-slate-500">Shown as chips under the description.</p>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Icon</Label>
                <IconPicker value={form.icon} onChange={(v) => setForm({ ...form, icon: v })} testId="svc-icon" />
              </div>
              <div>
                <Label>Slug</Label>
                <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="auto" />
              </div>
              <div>
                <Label>Sort order</Label>
                <Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
              <span className="text-sm">{form.is_active ? "Active (visible on public site)" : "Hidden"}</span>
            </div>
            <DialogFooter><Button type="submit" className="bg-primary hover:bg-primary/90">Save</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
